import React, { useMemo, useRef, useState } from 'react';
import { Title } from 'react-admin';
import type { ColDef } from 'ag-grid-community';
import AgGrid from '../../components/ag-grid/ag-grid';
import RenderControls from '../../components/ra-list/RenderControls';
import { formatLocalTime } from '../../components/ex_dayjs';
import type { AdditionalListingsIssue, FilterControl } from './types';
import AdditionalListingsIssueDetail from './additional-listings-issues-detail';

const AdditionalListingsIssuesView: React.FC = () => {
    const refInput = useRef<any>(null);
    const refbtnFilter = useRef<HTMLElement>(null);
    const [detailPopup, setDetailPopup] = useState<{ open: boolean; data: AdditionalListingsIssue | null }>({ open: false, data: null });

    const handleRowClicked = (params: any): void => {
        setDetailPopup({ open: true, data: params.data });
    };

    const defaultColDef = useMemo<ColDef>(() => ({
        sortable: true,
        resizable: true,
        flex: 1,
        minWidth: 80,
        filter: false,
        suppressMovable: true
    }), []);

    const colDefs: ColDef[] = [
        { field: 'id', headerName: 'ID', maxWidth: 100 },
        { field: 'siteId', headerName: 'SiteId', maxWidth: 150 },
        { field: 'type', headerName: 'Type', maxWidth: 120 },
        { field: 'pageType', headerName: 'PageType', maxWidth: 130 },
        { field: 'errorMessage', headerName: 'ErrorMessage', minWidth: 200 },
        {
            field: 'dumpUrl', headerName: 'DumpUrl', minWidth: 150,
            cellRenderer: (params: any) => {
                if (!params.value) return '';
                return <a href={params.value} target="_blank" rel="noopener noreferrer">{params.value}</a>;
            }
        },
        {
            field: 'url', headerName: 'Url', minWidth: 150,
            cellRenderer: (params: any) => {
                if (!params.value) return '';
                return <a href={params.value} target="_blank" rel="noopener noreferrer">{params.value}</a>;
            }
        },
        { field: 'reportedBy', headerName: 'ReportedBy', minWidth: 180 },
        {
            field: 'reportedAt', headerName: 'ReportedAt', maxWidth: 180,
            valueFormatter: (params: any) => params.value ? formatLocalTime(params.value) : ''
        },
    ];

    const controls: FilterControl[] = [
        { component: 'text', name: 'siteId_contains', label: 'Site ID', xs: 1.5 },
        { component: 'text', name: 'type_eq', label: 'Type', xs: 1 },
        { component: 'text', name: 'pageType_eq', label: 'Page Type', xs: 1 },
        { component: 'text', name: 'reportedBy_contains', label: 'Reported By', xs: 1.5 },
        { component: 'daterange', name: 'reportedAt', label: 'Reported At', xs: 2.5, isUTC: true },
    ];

    const handleSearch = (filterList: Record<string, unknown>): void => {
        refInput.current.customFilterSearch(filterList);
    };

    return (
        <div>
            <Title title={'Additional Listings Issues'} />
            <RenderControls
                controls={controls}
                onSearch={handleSearch}
                refFilter={refbtnFilter}
                filterInline={true}
                agSource='AdditionalListingsIssues'
                agSort={{ field: 'id', order: 'desc' }}
                exportFile={{
                    filename: `AdditionalListingsIssues-${formatLocalTime(new Date(), 'YY-MM-DD')}`,
                    columns: ['id', 'siteId', 'type', 'pageType', 'errorMessage', 'dumpUrl', 'url', 'reportedBy', 'reportedAt'],
                    limit: 1000000
                }}
            />
            <AgGrid
                ref={refInput}
                columnDefs={colDefs}
                defaultColDef={defaultColDef}
                rowHeight={45}
                list={'AdditionalListingsIssues'}
                onRowClicked={handleRowClicked}
            />
            {detailPopup.open && (
                <AdditionalListingsIssueDetail
                    open={detailPopup.open}
                    data={detailPopup.data}
                    onClose={() => setDetailPopup({ open: false, data: null })}
                />
            )}
        </div>
    );
};

export default AdditionalListingsIssuesView;
